import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { AuthorizeService } from '../../api-authorization/authorize.service';
import { Ladder } from '../../models/ladder.model';
import { Match } from '../../models/match.model';
import { Pairing } from '../../models/pairing.model';
import { Player } from '../../models/player.model';
import { UserService } from '../../services/user.service';
import { RecordService } from './record.service';
import { DateTime } from 'luxon';
import { RecordMatchBaseComponent } from './record.base.component';

@Component({
  selector: 'record-match',
  templateUrl: './record.component.html',
  styleUrls: ['./record.component.scss'],
})
export class RecordMatchComponent extends RecordMatchBaseComponent implements OnInit {

  public zero_pairing_msg: string = "Congratulations!!  You have played all of your matches(or we haven't started yet)";
  /** Natural-language score parsed by `/api/ai/score`; applied to opponent + grid. */
  public aiScorePhrase: string = '';
  public busyAiScore: boolean = false;
  /** Shown after AI parse attempt (success hints or failures). */
  public aiParseHint: string | null = null;
  /** Hint color: green for opponent matched, muted for parsing issues / manual follow-up. */
  public aiParseHintSuccess = false;

  constructor(recordSvc: RecordService,
    userSvc: UserService, http: HttpClient, fb: FormBuilder, router: Router) {
    super(recordSvc, userSvc, http, fb, router);

  }
  public ngOnInit() {
    const p1 = this.userSvc.getUser();
    const p2 = this.recordSvc.ladderInfo();
    const p3 = this.http.get<Pairing[]>('/pairing');

    forkJoin([p1, p2, p3]).subscribe(
      ([resp1, resp2, resp3]) => {
        this.player = new Player(resp1);

        this.ladderInfo = resp2;
        if (DateTime.fromISO(this.ladderInfo.endDate) < DateTime.local()) this.ladderOver = true;

        this.pairings.length = 0;
        for (let p of resp3) {
          this.pairings.push(new Pairing(p));
        }


        /////// the doubles players only wanted 10 out of 14 matches visible
        //if (this.ladderInfo.name.includes("doubles") || this.ladderInfo.name.includes("Doubles")) {
        //  this.pairings = this.pairings.filter((p) => p.round <= 10);
        //}
        this.pairings = this.pairings.sort((a, b) => a.round < b.round ? -1 : 1);


        // fix for iphone/ipad... need to assign first player for the select - null breaks things
        if (this.pairings.length > 0)
          this.matchForm.controls.opponentId.setValue(this.pairings[0].pairingPlayer.id);

        this.busy = false;
      },
      (err) => {

      }
    )
  };
  public saveMatch(evt: any) {
    if (this.busySave) return;
    this.busySave = true;
    let m = new Match(this.matchForm.value);
    this.recordSvc.recordMatch(m).subscribe(
      (resp) => {
        // redirect back to 
        this.router.navigate(['/home']);
      },
      (err) => {
        this.busySave = false;
        alert(err.error);
      }
    )
  }

  public saveRetiredWorker() {
    this.busySave = true;
    const m = new Match({ ...this.matchForm.value, Retired: true });
    this.recordSvc.recordMatch(m).subscribe(
      (resp) => {
        // redirect back to 
        this.router.navigate(['/home']);
      },
      (err) => {
        this.busySave = false;
        alert(err.error);
      }
    )
  }

  public saveForfeitWorker() {

    this.busySave = true;
    const m = new Match({ PairingId: this.matchForm.value.pairingId, Forfeit: true });
    this.recordSvc.recordMatch(m).subscribe(
      (resp) => {
        // redirect back to 
        this.router.navigate(['/home']);
      },
      (err) => {
        this.busySave = false;
        alert(err.error);
      }
    )
  }

  public back(evt: any): void {
    evt.preventDefault();
    this.router.navigate(['/home']);
  }

  public tryAiScoreParse(): void {
    const text = (this.aiScorePhrase || '').trim();
    if (!text) return;
    this.busyAiScore = true;
    this.aiParseHint = null;
    this.aiParseHintSuccess = false;
    this.http.post<unknown>('/api/ai/score', { text }).subscribe(
      (resp) => {
        this.busyAiScore = false;
        if (resp && typeof resp === 'object' && 'warning' in resp) {
          const o = resp as { warning?: string; message?: string; success?: boolean };
          const hint =
            (typeof o.message === 'string' && o.message ? o.message : null)
            ?? (typeof o.warning === 'string' && o.warning ? o.warning : null)
            ?? 'Could not parse AI response as JSON.';
          this.aiParseHint = hint;
          this.aiParseHintSuccess = o.success === true;
          return;
        }

        console.log('AI score parse response:', resp);
        const body = resp as Record<string, unknown>;
        if (body['success'] === false) {
          this.aiParseHint =
            typeof body['message'] === 'string' && body['message'].length ? String(body['message']) : 'Server rejected this parse.';
          this.aiParseHintSuccess = false;
          return;
        }

        this.applyAiScoreToForm(body);

        if (body['success'] === true && typeof body['message'] === 'string' && body['message'].length > 0) {
          this.aiParseHint = String(body['message']);
          this.aiParseHintSuccess = true;
        }
      },
      (err: { error?: { message?: string } }) => {
        console.error('AI score parse error:', err);
        this.busyAiScore = false;
        this.aiParseHint = typeof err?.error?.message === 'string' ? err.error.message : 'AI request failed.';
        this.aiParseHintSuccess = false;
      }
    );
  }

  /**
   * Maps `/api/ai/score` fields to the score entry form:
   * player1* → narrating player (your row); player2* → opponent row.
   */
  private applyAiScoreToForm(ai: Record<string, unknown>): void {
    if (!ai || typeof ai !== 'object') {
      this.aiParseHint = 'Unexpected AI response.';
      this.aiParseHintSuccess = false;
      return;
    }

    const pairing = this.findPairingFromAi(ai);
    if (!pairing) {
      const id = this.pick(ai, ['opponentid', 'OpponentId', 'opponentId']);
      const nm = this.pick(ai, ['opponentname', 'OpponentName', 'opponentName']);
      const hasSignal = id != null && String(id).trim() !== ''
        || (nm != null && String(nm).trim() !== '');
      this.aiParseHint = hasSignal
        ? 'Opponent did not match an open pairing. Choose the opponent from the dropdown manually.'
        : 'AI did not resolve an opponent. Choose the opponent and verify scores.';
      this.aiParseHintSuccess = false;
    } else {
      this.matchForm.patchValue({ opponentId: pairing.pairingPlayer.id }, { emitEvent: true });
      this.aiParseHint = `Selected opponent: ${pairing.pairingPlayer.name}`;
      this.aiParseHintSuccess = true;
    }

    const gameStr = (...aliasKeys: string[]) =>
      this.clampGamesStr(this.pick(ai, aliasKeys));

    const mainPatch: Record<string, string> = {
      Set1Player1Score: gameStr('player1set1', 'Player1Set1'),
      Set1Player2Score: gameStr('player2set1', 'Player2Set1'),
      Set2Player1Score: gameStr('player1set2', 'Player1Set2'),
      Set2Player2Score: gameStr('player2set2', 'Player2Set2'),
      Set3Player1Score: gameStr('player1set3', 'Player1Set3'),
      Set3Player2Score: gameStr('player2set3', 'Player2Set3'),
    };
    this.matchForm.patchValue(mainPatch);

    const tiePatch = {
      Set1Player1Tie: this.tieStr(this.pick(ai, ['player1tiebreak1', 'player1Tiebreak1'])),
      Set1Player2Tie: this.tieStr(this.pick(ai, ['player2tiebreak1', 'player2Tiebreak1'])),
      Set2Player1Tie: this.tieStr(this.pick(ai, ['player1tiebreak2', 'player1Tiebreak2'])),
      Set2Player2Tie: this.tieStr(this.pick(ai, ['player2tiebreak2', 'player2Tiebreak2'])),
      Set3Player1Tie: this.tieStr(this.pick(ai, ['player1tiebreak3', 'player1Tiebreak3'])),
      Set3Player2Tie: this.tieStr(this.pick(ai, ['player2tiebreak3', 'player2Tiebreak3'])),
    };

    // Tiebreaker dropdowns render only after 7–6 is applied; defer one tick so *ngIf can create controls.
    setTimeout(() => {
      this.matchForm.patchValue(tiePatch);
      if (+mainPatch.Set3Player1Score === 0 && +mainPatch.Set3Player2Score === 0) {
        this.matchForm.patchValue({
          Set3Player1Tie: '0',
          Set3Player2Tie: '0',
        });
      }
    }, 0);
  }

  private findPairingFromAi(ai: Record<string, unknown>): Pairing | undefined {
    const idRaw = this.pick(ai, ['opponentid', 'OpponentId', 'opponentId']);
    const sid = idRaw != null && idRaw !== '' ? String(idRaw).trim() : '';
    if (sid) {
      let p = this.pairings.find((x) => x.pairingPlayer.id === sid);
      if (p) {
        return p;
      }
      p = this.pairings.find((x) => x.pairingPlayer.id.toLowerCase() === sid.toLowerCase());
      if (p) {
        return p;
      }
    }
    const rawName = this.pick(ai, ['opponentname', 'OpponentName', 'opponentName']);
    const nameNorm = rawName != null && rawName !== '' ? String(rawName).trim().toLowerCase() : '';
    if (!nameNorm) {
      return undefined;
    }
    const exact = this.pairings.find((x) => x.pairingPlayer.name.trim().toLowerCase() === nameNorm);
    if (exact) {
      return exact;
    }
    return this.pairings.find((p) => {
      const pn = p.pairingPlayer.name.toLowerCase();
      if (pn.includes(nameNorm)) {
        return true;
      }
      const parts = nameNorm.split(/\s+/).filter((w) => w.length > 1);
      return parts.length > 0 && parts.every((w) => pn.includes(w));
    });
  }

  private pick(ai: Record<string, unknown>, keys: string[]): unknown {
    for (const k of keys) {
      if (k in ai && ai[k] !== undefined && ai[k] !== null) {
        return ai[k];
      }
    }
    const map = Object.keys(ai);
    for (const k of keys) {
      const lower = k.toLowerCase();
      const alt = map.find((x) => x.toLowerCase() === lower);
      if (alt !== undefined && ai[alt] !== undefined && ai[alt] !== null) {
        return ai[alt];
      }
    }
    return undefined;
  }

  private clampGamesStr(v: unknown): string {
    const n = Math.round(Number(v));
    const maxGames = Math.max(...this.possibleScores);
    if (Number.isNaN(n)) {
      return '0';
    }
    return String(Math.min(maxGames, Math.max(0, n)));
  }

  private tieStr(v: unknown): string {
    const n = Math.round(Number(v));
    const maxTie = Math.max(...this.possibleTieScores);
    if (Number.isNaN(n)) {
      return '0';
    }
    return String(Math.min(maxTie, Math.max(0, n)));
  }
}
